export const MAXIMUM_STRIPE_TRANSACTION_AMOUNT_IN_CENTS = 99999999;
export const CENTS_IN_DOLLAR = 100;
export const ZERO = 0;
const MINIMUM_CENTS_AFFECTED_BY_COMPACT_NOTATION = 99950;

/**
 * @param value - The amount in cents
 * @returns A formatted dollar string
 *
 * @example
 * centsToWholeDollar(0) // => "$0"
 * centsToWholeDollar(250500) // => "$2.51K"
 * centsToWholeDollar(1500450500) // => "$15M"
 */
export const centsToCompactDollar = (value: number) => {
  if (value < MINIMUM_CENTS_AFFECTED_BY_COMPACT_NOTATION) {
    return centsToWholeDollar(value);
  }

  try {
    return Intl.NumberFormat('en-US', {
      currency: 'USD',
      maximumSignificantDigits: 3,
      notation: 'compact',
      style: 'currency',
    }).format(value / CENTS_IN_DOLLAR);
  } catch (_e) {
    return centsToWholeDollar(value);
  }
};

/**
 * @param value - The amount in cents
 * @returns The amount in dollars
 *
 * @example
 * centsToDecimal(2025) // => 20.25
 * centsToDecimal(3500000) // => 35000
 */
export const centsToDecimal = (value: number): number => {
  return value / CENTS_IN_DOLLAR;
};

/**
 * @param value - The amount in cents
 * @returns A formatted string with the amount in dollars
 *
 * @example
 * centsToDollar(2022) // => "$20.22"
 * centsToDollar(undefined) // => "--"
 */
export const centsToDollar = (value?: number): string => {
  const currencyFormatter = new Intl.NumberFormat('en-US', {
    currency: 'USD',
    minimumFractionDigits: 2,
    style: 'currency',
  });
  if (!value && value !== ZERO) {
    return '--';
  }
  return currencyFormatter.format(value / CENTS_IN_DOLLAR);
};

/**
 * @param value - The amount in cents
 * @returns A formatted string with the amount rounded to nearest whole dollar
 *
 * @example
 * centsToWholeDollar(2025) // => "$20"
 * centsToWholeDollar(0) // => "$0"
 * centsToWholeDollar(undefined) // => "--"
 */
export const centsToWholeDollar = (value?: number): string => {
  const currencyFormatter = new Intl.NumberFormat('en-US', {
    currency: 'USD',
    maximumFractionDigits: 0,
    minimumFractionDigits: 0,
    style: 'currency',
  });
  if (!value && value !== ZERO) {
    return '--';
  }
  return currencyFormatter.format(value / CENTS_IN_DOLLAR);
};

const formatMinorUnits = (
  value: number | undefined,
  currency: string,
  fractionDigits: number,
): string => {
  if (!value && value !== ZERO) {
    return '--';
  }

  const amount = value / CENTS_IN_DOLLAR;
  try {
    return new Intl.NumberFormat('en-US', {
      currency,
      maximumFractionDigits: fractionDigits,
      minimumFractionDigits: fractionDigits,
      style: 'currency',
    }).format(amount);
  } catch (_e) {
    // Intl throws for a code it doesn't know. Showing the code beats a "$" that would be wrong.
    return `${amount.toFixed(fractionDigits)} ${currency}`;
  }
};

/**
 * Like `centsToDollar`, for an amount in any currency that has 100 minor units (USD, GBP,
 * EUR, CAD...). Use it for amounts that come with their own currency, such as Lightspeed's.
 *
 * @param value - The amount in minor units (cents, pence)
 * @param currency - ISO 4217 code
 *
 * @example
 * formatMoney(2022, 'USD') // => "$20.22"
 * formatMoney(2022, 'GBP') // => "£20.22"
 * formatMoney(undefined, 'GBP') // => "--"
 */
export const formatMoney = (value: number | undefined, currency: string) =>
  formatMinorUnits(value, currency, 2);

/**
 * Like `centsToWholeDollar`, for any currency with 100 minor units.
 *
 * @example
 * formatWholeMoney(10131, 'GBP') // => "£101"
 */
export const formatWholeMoney = (value: number | undefined, currency: string) =>
  formatMinorUnits(value, currency, 0);

/**
 * @param value - The amount in dollars
 * @returns The amount in cents
 *
 * @example
 * wholeDollarsToCents(5) // => 500
 */
export const wholeDollarsToCents = (value: number): number =>
  Number(value) * CENTS_IN_DOLLAR;

/**
 * @param value - The amount in dollars
 * @returns A formatted string with the dollar amount
 *
 * @example
 * formatWholeDollars(5) // => $5
 * formatWholeDollars(5000) // => $5,000
 */
export const formatWholeDollars = (value: number): string =>
  centsToWholeDollar(wholeDollarsToCents(value));

/**
 * @param value - The amount in cents
 * @returns A string with the dollar amount
 *
 * @example
 * centsToDollarNumber(2025) // => '20.25'
 * centsToDollarNumber(0) // => '0.0'
 * centsToDollarNumber(-1000) // => '-10.00'
 * centsToDollarNumber(undefined) // => ''
 * centsToDollarNumber(null) // => ''
 */
export const centsToDollarNumber = (value?: number): string => {
  if (!value && value !== ZERO) {
    return '';
  }
  return (value / CENTS_IN_DOLLAR).toFixed(2);
};
